import { useState } from "react";
import { Save, Plus, Trash2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const inputClass =
  "rounded-full border border-slate-200 px-4 py-2 text-sm outline-none transition focus:border-[#2563EB] disabled:bg-slate-50 disabled:text-slate-300";

export const HorairesView = ({ est, reload }) => {
  const { t, lang } = useLanguage();
  const [rows, setRows] = useState(() => (est.hours_detail || []).map((h) => ({ ...h })));
  const [specials, setSpecials] = useState(() => (est.special_hours || []).map((s) => ({ ...s })));
  const [newSpecial, setNewSpecial] = useState({ date: "", label: "", value: "", closed: false });
  const [busy, setBusy] = useState(false);

  const setValue = (i, value) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, value } : row)));
  const toggleClosed = (i) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, closed: !row.closed, value: !row.closed ? "Fermé" : "" } : row)));

  const addSpecial = () => {
    if (!newSpecial.date || (!newSpecial.closed && !newSpecial.value.trim())) {
      toast.error(t("Ajoute une date et des horaires (ou coche Fermé)"));
      return;
    }
    setSpecials((s) => [...s, { ...newSpecial, value: newSpecial.closed ? "Fermé" : newSpecial.value.trim() }].sort((a, b) => a.date.localeCompare(b.date)));
    setNewSpecial({ date: "", label: "", value: "", closed: false });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/pro/establishment", { hours_detail: rows, special_hours: specials });
      toast.success(t("Horaires mis à jour — visibles sur ta page publique"));
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 grid max-w-4xl gap-5 lg:grid-cols-2" data-testid="pro-horaires-view">
      {/* HORAIRES HEBDOMADAIRES */}
      <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
        <h2 className="text-base font-bold text-slate-900">{t("Horaires d'ouverture")}</h2>
        <p className="mt-1 text-xs text-slate-500">{t("Définis tes horaires pour chaque jour de la semaine.")}</p>
        <div className="mt-5 space-y-3">
          {rows.map((row, i) => (
            <div key={row.day} className="flex items-center gap-3" data-testid={`hours-row-${i}`}>
              <span className="w-24 shrink-0 text-sm font-medium text-slate-700">{row.day}</span>
              <input
                data-testid={`hours-input-${i}`}
                value={row.closed ? "" : row.value}
                onChange={(e) => setValue(i, e.target.value)}
                disabled={row.closed}
                placeholder={t("Ex. : 17h00 – 01h00")}
                className={`${inputClass} min-w-0 flex-1`}
              />
              <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs text-slate-500">
                <input
                  type="checkbox"
                  data-testid={`hours-closed-${i}`}
                  checked={row.closed}
                  onChange={() => toggleClosed(i)}
                  className="h-4 w-4 rounded border-slate-300 accent-[#2563EB]"
                />
                Fermé
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* HORAIRES SPÉCIAUX */}
      <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
          <CalendarClock className="h-4 w-4 text-[#2563EB]" />
          Horaires spéciaux
        </h2>
        <p className="mt-1 text-xs text-slate-500">{t("Exceptions pour une date précise (congé, événement, fermeture exceptionnelle).")}</p>

        <div className="mt-4 space-y-2">
          {specials.length === 0 ? (
            <p data-testid="special-empty" className="rounded-2xl bg-[#F8FAFC] p-5 text-center text-xs text-slate-400">
              {t("Aucun horaire spécial. Ajoute une exception ci-dessous.")}
            </p>
          ) : (
            specials.map((s, i) => (
              <div key={s.date + i} data-testid={`special-row-${i}`} className="flex items-center gap-2.5 rounded-2xl border border-slate-100 px-3.5 py-2.5">
                <span className="rounded-full bg-[#EFF6FF] px-2.5 py-1 text-[10px] font-bold text-[#2563EB]">
                  {new Date(`${s.date}T12:00:00`).toLocaleDateString("fr-CA", { day: "numeric", month: "short" })}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-slate-800">{s.label || "Horaire spécial"}</p>
                  <p className={`text-[11px] ${s.closed ? "font-semibold text-red-500" : "text-slate-500"}`}>{s.closed ? "Fermé" : s.value}</p>
                </div>
                <button
                  type="button"
                  data-testid={`special-delete-${i}`}
                  onClick={() => setSpecials((arr) => arr.filter((_, idx) => idx !== i))}
                  aria-label={t("Retirer")}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition hover:border-red-300 hover:text-red-500"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))
          )}
        </div>

        <div className="mt-4 space-y-2.5 rounded-2xl bg-[#F8FAFC] p-4">
          <div className="grid grid-cols-2 gap-2.5">
            <input data-testid="special-date" type="date" value={newSpecial.date} onChange={(e) => setNewSpecial((s) => ({ ...s, date: e.target.value }))} className={`${inputClass} w-full`} />
            <input data-testid="special-label" value={newSpecial.label} onChange={(e) => setNewSpecial((s) => ({ ...s, label: e.target.value }))} placeholder={t("Occasion (ex. : Noël)")} className={`${inputClass} w-full`} />
          </div>
          <div className="flex items-center gap-2.5">
            <input
              data-testid="special-value"
              value={newSpecial.closed ? "" : newSpecial.value}
              onChange={(e) => setNewSpecial((s) => ({ ...s, value: e.target.value }))}
              disabled={newSpecial.closed}
              placeholder={t("Ex. : 18h00 – 23h00")}
              className={`${inputClass} min-w-0 flex-1`}
            />
            <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs text-slate-500">
              <input type="checkbox" data-testid="special-closed" checked={newSpecial.closed} onChange={() => setNewSpecial((s) => ({ ...s, closed: !s.closed }))} className="h-4 w-4 rounded border-slate-300 accent-[#2563EB]" />
              Fermé
            </label>
          </div>
          <button type="button" data-testid="special-add" onClick={addSpecial} className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-[#2563EB]">
            <Plus className="h-3.5 w-3.5" />
            Ajouter cette exception
          </button>
        </div>
      </div>

      <div className="lg:col-span-2">
        <button
          type="button"
          data-testid="hours-save-button"
          onClick={save}
          disabled={busy}
          className="flex items-center gap-2 rounded-full bg-[#2563EB] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#1D4ED8] active:scale-95 disabled:opacity-60"
        >
          <Save className="h-4 w-4" />
          {busy ? "Enregistrement…" : "Enregistrer les horaires"}
        </button>
      </div>
    </div>
  );
};
